import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfDay,
  isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Ban, Loader2, Lock, Unlock, Plus, X, AlertTriangle } from 'lucide-react';
import useDisponibility from '../../hooks/useDisponibility';
import './calendarView.css';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS HIJRI / JOURS FÉRIÉS TUNISIENS
// ─────────────────────────────────────────────────────────────────────────────

function hijriToGregorian(hYear, hMonth, hDay) {
  const jd =
    Math.floor((11 * hYear + 3) / 30) +
    354 * hYear +
    30 * hMonth -
    Math.floor((hMonth - 1) / 2) +
    hDay +
    1948440 -
    385;
  let l = jd + 68569;
  const n = Math.floor((4 * l) / 146097);
  l = l - Math.floor((146097 * n + 3) / 4);
  const i = Math.floor((4000 * (l + 1)) / 1461001);
  l = l - Math.floor((1461 * i) / 4) + 31;
  const j = Math.floor((80 * l) / 2447);
  const day = l - Math.floor((2447 * j) / 80);
  l = Math.floor(j / 11);
  const month = j + 2 - 12 * l;
  const year = 100 * (n - 49) + i + l;
  return new Date(year, month - 1, day);
}

function gregorianToHijriYear(gDate) {
  return Math.floor((gDate.getFullYear() - 622) * (33 / 32)) + 1;
}

function getIslamicHoliday(gYear, hMonth, hDay, offsetDays = 0) {
  const hYear = gregorianToHijriYear(new Date(gYear, 0, 1));
  const dates = [];
  for (let y = hYear - 1; y <= hYear + 1; y++) {
    const d = hijriToGregorian(y, hMonth, hDay);
    const shifted = new Date(d);
    shifted.setDate(shifted.getDate() + offsetDays);
    if (shifted.getFullYear() === gYear) dates.push(shifted);
  }
  return dates;
}

function getTunisianHolidays(year, t) {
  const holidays = [];
  const fixed = [
    { month: 0,  day: 1,  key: 'newYear' },
    { month: 0,  day: 14, key: 'revolutionDay' },
    { month: 2,  day: 20, key: 'independenceDay' },
    { month: 3,  day: 9,  key: 'martyrsDay' },
    { month: 4,  day: 1,  key: 'labourDay' },
    { month: 6,  day: 25, key: 'republicDay' },
    { month: 7,  day: 13, key: 'womenDay' },
    { month: 9,  day: 15, key: 'evacuationDay' },
    { month: 11, day: 17, key: 'decRevolution' },
  ];
  fixed.forEach(({ month, day, key }) =>
    holidays.push({ date: new Date(year, month, day), name: t(`holidays.${key}`) })
  );
  getIslamicHoliday(year, 10, 1).forEach((d)    => holidays.push({ date: d, name: t('holidays.eidAlFitr1') }));
  getIslamicHoliday(year, 10, 1, 1).forEach((d) => holidays.push({ date: d, name: t('holidays.eidAlFitr2') }));
  getIslamicHoliday(year, 12, 10).forEach((d)   => holidays.push({ date: d, name: t('holidays.eidAlAdha1') }));
  getIslamicHoliday(year, 12, 10, 1).forEach((d)=> holidays.push({ date: d, name: t('holidays.eidAlAdha2') }));
  getIslamicHoliday(year, 1, 1).forEach((d)     => holidays.push({ date: d, name: t('holidays.islamicNewYear') }));
  getIslamicHoliday(year, 3, 12).forEach((d)    => holidays.push({ date: d, name: t('holidays.mawlid') }));
  return holidays;
}

// Cache par langue pour éviter de recalculer à chaque render
const holidayCache = {};
function getHolidaysForYear(year, t, lang) {
  const cacheKey = `${year}_${lang}`;
  if (!holidayCache[cacheKey]) holidayCache[cacheKey] = getTunisianHolidays(year, t);
  return holidayCache[cacheKey];
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS DISPONIBILITÉ
// ─────────────────────────────────────────────────────────────────────────────

function hasSlotOnDate(rawSlots, d) {
  const target = format(d, 'yyyy-MM-dd');
  return rawSlots.some((slot) => String(slot.startTime ?? '').substring(0, 10) === target);
}

// ─────────────────────────────────────────────────────────────────────────────
// LEGEND ITEM
// ─────────────────────────────────────────────────────────────────────────────

function LegendItem({ cls, label }) {
  return (
    <div className="cal-legend__item">
      <div className={`cal-legend__swatch cal-legend__swatch${cls}`} />
      <span>{label}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BUSINESS ERROR MODAL
// ─────────────────────────────────────────────────────────────────────────────

function BusinessErrorModal({ open, title, message, onClose, t }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--smart" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <p className="modal__title">{title}</p>
            <p className="modal__date">{t('modal.businessRule')}</p>
          </div>
          <button className="modal__close-btn" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>
        <div className="modal__section" style={{ borderBottom: 'none' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertTriangle size={18} color="#f97316" style={{ marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#374151' }}>
              {message}
            </p>
          </div>
          <div className="modal__actions" style={{ marginTop: 18 }}>
            <button className="modal__btn modal__btn--confirm" type="button" onClick={onClose}>
              {t('modal.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DAY MANAGEMENT MODAL
// ─────────────────────────────────────────────────────────────────────────────

function DayManagementModal({
  date, blockedSlots, canBlockThisDate,
  onClose, onBlock, onUnblock, loading, onBusinessError, t,
}) {
  const [showAddForm, setShowAddForm]   = useState(false);
  const [startTime, setStartTime]       = useState('09:00');
  const [endTime, setEndTime]           = useState('17:00');
  const [reason, setReason]             = useState('');
  const [error, setError]               = useState(null);
  const [unblockingId, setUnblockingId] = useState(null);

  const hasBlocked = blockedSlots.length > 0;

  const formatRange = (startISO, endISO) => {
    const fmt = (iso) => {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    return `${fmt(startISO)} – ${fmt(endISO)}`;
  };

  const handleUnblock = async (slot) => {
    setUnblockingId(slot.id);
    try { await onUnblock(slot.id); }
    catch (err) { console.error('[Modal] unblock error:', err); }
    finally { setUnblockingId(null); }
  };

  const handleOpenAddForm = () => {
    if (!canBlockThisDate) {
      onBusinessError?.(t('modal.errors.requireAvailability'));
      return;
    }
    setShowAddForm(true);
  };

  const handleBlock = async () => {
    if (!canBlockThisDate) {
      onBusinessError?.(t('modal.errors.requireAvailability'));
      return;
    }
    if (!startTime || !endTime) { setError(t('modal.errors.fillBothTimes')); return; }
    if (startTime >= endTime)   { setError(t('modal.errors.endAfterStart')); return; }

    setError(null);
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      await onBlock({
        startTime: `${dateStr}T${startTime}:00`,
        endTime:   `${dateStr}T${endTime}:00`,
        reason:    reason.trim() || undefined,
      });
      setShowAddForm(false);
      setStartTime('09:00');
      setEndTime('17:00');
      setReason('');
    } catch (err) {
      setError(err?.message ?? t('modal.errors.blockError'));
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal modal--smart" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <p className="modal__title">{t('modal.manageDay')}</p>
            <p className="modal__date">{format(date, 'EEEE, d MMMM yyyy')}</p>
          </div>
          <button className="modal__close-btn" onClick={onClose} type="button" disabled={loading}>
            <X size={18} />
          </button>
        </div>

        {hasBlocked && (
          <div className="modal__section">
            <p className="modal__section-title">
              <Lock size={13} style={{ display: 'inline', marginRight: 5 }} />
              {t('modal.blockedSlots')}
            </p>
            <div className="modal__blocked-list">
              {blockedSlots.map((slot) => (
                <div key={slot.id} className="modal__blocked-item">
                  <span className="modal__blocked-range">{formatRange(slot.startTime, slot.endTime)}</span>
                  <button
                    className="modal__unblock-btn"
                    type="button"
                    disabled={!!unblockingId || loading}
                    onClick={() => handleUnblock(slot)}
                  >
                    {unblockingId === slot.id
                      ? <><Loader2 size={13} className="modal__spin" /> {t('modal.unblocking')}</>
                      : <><Unlock size={13} /> {t('modal.unblock')}</>
                    }
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!showAddForm ? (
          <button className="modal__add-btn" type="button" disabled={loading} onClick={handleOpenAddForm}>
            <Plus size={14} />
            {t('modal.blockSlot')}
          </button>
        ) : (
          <div className="modal__section modal__section--form">
            <p className="modal__section-title">
              <Ban size={13} style={{ display: 'inline', marginRight: 5 }} />
              {t('modal.newBlock')}
            </p>
            <div className="modal__row">
              <div className="modal__field">
                <label className="modal__label">{t('modal.startTime')}</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="modal__input" disabled={loading} />
              </div>
              <div className="modal__field">
                <label className="modal__label">{t('modal.endTime')}</label>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="modal__input" disabled={loading} />
              </div>
            </div>
            <div className="modal__field">
              <label className="modal__label">{t('modal.reason')}</label>
              <input
                type="text"
                placeholder={t('modal.reasonPlaceholder')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="modal__input"
                disabled={loading}
              />
            </div>
            {error && <div className="modal__error"><p>{error}</p></div>}
            <div className="modal__actions">
              <button
                onClick={() => { setShowAddForm(false); setError(null); }}
                className="modal__btn modal__btn--cancel"
                type="button"
                disabled={loading}
              >
                {t('modal.cancel')}
              </button>
              <button onClick={handleBlock} className="modal__btn modal__btn--confirm" type="button" disabled={loading}>
                {loading ? <><Loader2 size={14} className="modal__spin" /> {t('modal.blocking')}</> : t('modal.block')}
              </button>
            </div>
          </div>
        )}

        {!hasBlocked && !showAddForm && (
          <p className="modal__empty-hint">{t('modal.noBlockedSlots')}</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export default function CalendarView({ medecinId, events = [], onDateSelect, rawSlots = [] }) {
  const { t, i18n } = useTranslation('availability');

  const [currentMonth, setCurrentMonth]   = useState(new Date());
  const [selectedDate, setSelectedDate]   = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [blockedDays, setBlockedDays]     = useState([]);
  const [blockLoading, setBlockLoading]   = useState(false);
  const [businessError, setBusinessError] = useState('');

  const { loadSlots, blockRange, deleteSlot, loadBlockedSlots } = useDisponibility({
    medecinId,
    date: selectedDate ?? new Date(),
  });

  useEffect(() => {
    if (!medecinId) return;
    let cancelled = false;

    loadBlockedSlots(medecinId)
      .then((action) => {
        if (cancelled) return;
        const slots = action?.payload?.data?.slots ?? [];
        setBlockedDays(slots.map((s) => ({
          id: s.id,
          date: new Date(s.startTime),
          startTime: s.startTime,
          endTime: s.endTime,
        })));
      })
      .catch((err) => console.error('[CalendarView] loadBlockedSlots error:', err));

    return () => { cancelled = true; };
  }, [medecinId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showModal || businessError) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showModal, businessError]);

  const monthStart  = startOfMonth(currentMonth);
  const monthEnd    = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const monthFormatter = new Intl.DateTimeFormat(i18n.language || 'en', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Noms des jours de la semaine traduits
  const DOW_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  const getHolidaysForDate = (d) =>
    getHolidaysForYear(d.getFullYear(), t, i18n.language).filter((h) => isSameDay(h.date, d));

  const isPublicHoliday = (d) => getHolidaysForDate(d).length > 0;
  const getHolidayNames = (d) => getHolidaysForDate(d).map((h) => h.name).join(' · ');

  const hasEvent = (d) => {
    const dayStart = startOfDay(d);
    return events.some((e) => {
      const s  = startOfDay(new Date(e.startDate));
      const en = startOfDay(new Date(e.endDate));
      return dayStart >= s && dayStart <= en;
    });
  };

  const isBlocked = useCallback(
    (d) => blockedDays.some((b) => isSameDay(new Date(b.date), d)),
    [blockedDays]
  );

  const getBlockedSlotsForDate = useCallback(
    (d) => blockedDays.filter((b) => isSameDay(new Date(b.date), d)),
    [blockedDays]
  );

  const canBlockDate = useCallback(
    (d) => hasSlotOnDate(rawSlots, d),
    [rawSlots]
  );

  const getDayClass = (d) => {
    let cls = 'cal-day';
    if (isToday(d))         cls += ' cal-day--today';
    if (isPublicHoliday(d)) return `${cls} cal-day--holiday`;
    if (hasEvent(d))        return `${cls} cal-day--event`;
    if (isBlocked(d))       return `${cls} cal-day--blocked`;
    if (hasSlotOnDate(rawSlots, d)) return `${cls} cal-day--available`;
    return cls;
  };

  const handleDayClick = (d) => {
    if (!isSameMonth(d, currentMonth)) return;
    setSelectedDate(d);
    if (onDateSelect) onDateSelect(d);
    setShowModal(true);
  };

  const handleBlock = useCallback(
    async ({ startTime, endTime, reason }) => {
      if (!selectedDate || !canBlockDate(selectedDate)) {
        setBusinessError(t('modal.errors.requireAvailability'));
        return;
      }
      setBlockLoading(true);
      try {
        const optimisticId = `optimistic-${Date.now()}`;
        setBlockedDays((prev) => [
          ...prev,
          { id: optimisticId, date: new Date(startTime), startTime, endTime },
        ]);

        await blockRange({ medecinId, startTime, endTime, reason });

        const action = await loadBlockedSlots(medecinId);
        const freshSlots = action?.payload?.data?.slots ?? [];
        setBlockedDays(freshSlots.map((s) => ({
          id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime,
        })));

        const dateStr = startTime.split('T')[0];
        await loadSlots(medecinId, dateStr);
      } catch (err) {
        setBlockedDays((prev) => prev.filter((b) => !String(b.id).startsWith('optimistic-')));
        throw err;
      } finally {
        setBlockLoading(false);
      }
    },
    [selectedDate, canBlockDate, medecinId, blockRange, loadBlockedSlots, loadSlots, t]
  );

  const handleUnblock = useCallback(
    async (slotId) => {
      setBlockedDays((prev) => prev.filter((b) => b.id !== slotId));
      try {
        await deleteSlot(slotId);
        const action = await loadBlockedSlots(medecinId);
        const freshSlots = action?.payload?.data?.slots ?? [];
        setBlockedDays(freshSlots.map((s) => ({
          id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime,
        })));
      } catch (err) {
        console.error('[CalendarView] unblock error:', err);
        const action = await loadBlockedSlots(medecinId);
        const freshSlots = action?.payload?.data?.slots ?? [];
        setBlockedDays(freshSlots.map((s) => ({
          id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime,
        })));
        throw err;
      }
    },
    [deleteSlot, loadBlockedSlots, medecinId]
  );

  const firstDayOffset = monthStart.getDay();
  const emptySlots     = Array(firstDayOffset).fill(null);

  return (
    <>
      <div className="cal-card">
        <div className="cal-header">
          <button className="cal-nav-btn" onClick={prevMonth} type="button" aria-label="Previous month">
            <ChevronLeft size={16} />
          </button>

          <div className="cal-header__title-wrap">
            <p className="cal-header__title">{monthFormatter.format(currentMonth)}</p>
            <p className="cal-header__subtitle">{t('calendar.title')}</p>
          </div>

          <button className="cal-nav-btn" onClick={nextMonth} type="button" aria-label="Next month">
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="cal-body">
          <div className="cal-dow">
            {DOW_KEYS.map((key) => (
              <div key={key} className="cal-dow__label">
                {t(`calendar.days.${key}`)}
              </div>
            ))}
          </div>

          <div className="cal-grid">
            {emptySlots.map((_, i) => <div key={`e-${i}`} className="cal-empty" />)}

            {daysInMonth.map((date) => {
              const holidayName = isPublicHoliday(date) ? getHolidayNames(date) : null;
              const blocked = isBlocked(date);

              return (
                <button
                  key={date.toISOString()}
                  className={getDayClass(date)}
                  onClick={() => handleDayClick(date)}
                  title={holidayName ?? undefined}
                  type="button"
                >
                  <span className="cal-day__number">{format(date, 'd')}</span>
                  {isToday(date) && <span className="cal-day__today-label">{t('calendar.today')}</span>}
                  {blocked && <Ban className="cal-day__ban" />}
                  {holidayName && <span className="cal-day__tooltip">{holidayName}</span>}
                </button>
              );
            })}
          </div>

          <div className="cal-legend">
            <div className="cal-legend__items">
              <LegendItem cls="--available"   label={t('calendar.legend.available')} />
              <LegendItem cls="--holiday"     label={t('calendar.legend.holiday')} />
              <LegendItem cls="--event"       label={t('calendar.legend.event')} />
              <LegendItem cls="--blocked"     label={t('calendar.legend.blocked')} />
              <LegendItem cls="--unavailable" label={t('calendar.legend.unavailable')} />
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedDate && (
        <DayManagementModal
          date={selectedDate}
          blockedSlots={getBlockedSlotsForDate(selectedDate)}
          canBlockThisDate={canBlockDate(selectedDate)}
          onClose={() => setShowModal(false)}
          onBlock={handleBlock}
          onUnblock={handleUnblock}
          loading={blockLoading}
          onBusinessError={(msg) => setBusinessError(msg)}
          t={t}
        />
      )}

      <BusinessErrorModal
        open={!!businessError}
        title={t('businessError.title')}
        message={businessError}
        onClose={() => setBusinessError('')}
        t={t}
      />
    </>
  );
}